import React, { useState } from 'react';
import Input from '../common/Input';
import Button from '../common/Button';

const SubscriptionPlanForm = ({ plan, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: plan?.name || '',
    description: plan?.description || '',
    duration: plan?.duration || 30,
    price: plan?.price || '',
    discountPercentage: plan?.benefits?.discountPercentage || 0,
    freeItems: plan?.benefits?.freeItems || 0,
    bundleClothes: plan?.benefits?.bundleClothes || 0,
    expressDeliveryFree: plan?.benefits?.expressDeliveryFree || false,
    priorityService: plan?.benefits?.priorityService || false,
    isActive: plan?.isActive !== undefined ? plan.isActive : true
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Plan name is required';
    }

    if (!formData.duration || formData.duration < 1) {
      newErrors.duration = 'Duration must be at least 1 day';
    }

    if (!formData.price || formData.price < 0) {
      newErrors.price = 'Valid price is required';
    }

    if (formData.discountPercentage < 0 || formData.discountPercentage > 100) {
      newErrors.discountPercentage = 'Discount must be between 0 and 100';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (validate()) {
      const submitData = {
        name: formData.name,
        description: formData.description,
        duration: parseInt(formData.duration),
        price: parseFloat(formData.price),
        benefits: {
          discountPercentage: parseFloat(formData.discountPercentage),
          freeItems: parseInt(formData.freeItems),
          bundleClothes: parseInt(formData.bundleClothes),
          expressDeliveryFree: formData.expressDeliveryFree,
          priorityService: formData.priorityService
        },
        isActive: formData.isActive
      };

      onSubmit(submitData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="form">
      <div className="form-row">
        <Input
          label="Plan Name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          error={errors.name}
          placeholder="e.g., Premium Monthly, Family Plan"
          required
        />
        <Input
          label="Duration (Days)"
          name="duration"
          type="number"
          value={formData.duration}
          onChange={handleChange}
          error={errors.duration}
          placeholder="30, 90, 180"
          required
        />
      </div>

      <div className="form-row">
        <Input
          label="Price (KWD)"
          name="price"
          type="number"
          step="0.001"
          value={formData.price}
          onChange={handleChange}
          error={errors.price}
          placeholder="15.000"
          required
        />
        <Input
          label="Discount Percentage (%)"
          name="discountPercentage"
          type="number"
          step="1"
          value={formData.discountPercentage}
          onChange={handleChange}
          error={errors.discountPercentage}
          placeholder="10, 15, 20"
        />
      </div>

      <div className="form-row">
        <div className="input-group">
          <label className="input-label">Description</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="input"
            rows="3"
            placeholder="Perfect for families and regular customers. Includes exclusive benefits..."
          />
        </div>
      </div>

      <div className="form-section">
        <h4 style={{ marginBottom: 'var(--spacing-md)', color: 'var(--gray-700)' }}>
          Additional Benefits
        </h4>
        
        <div className="form-row">
          <Input
            label="Free Items"
            name="freeItems"
            type="number"
            value={formData.freeItems}
            onChange={handleChange}
            placeholder="5, 10, 15"
          />
          <Input
            label="Bundle Clothes (Items Included)"
            name="bundleClothes"
            type="number"
            value={formData.bundleClothes}
            onChange={handleChange}
            placeholder="10, 20, 50"
          />
        </div>

        <div className="form-row">
          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="expressDeliveryFree"
                checked={formData.expressDeliveryFree}
                onChange={handleChange}
              />
              <span>Free Express Delivery</span>
            </label>
          </div>

          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="priorityService"
                checked={formData.priorityService}
                onChange={handleChange}
              />
              <span>Priority Service</span>
            </label>
          </div>
        </div>

        <div className="form-row">
          <div className="checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
              />
              <span>Active Plan (Visible to Customers)</span>
            </label>
          </div>
        </div>
      </div>

      <div className="pricing-examples" style={{ 
        background: '#f0f9ff', 
        padding: '12px 16px', 
        borderRadius: '8px', 
        marginTop: '16px',
        border: '1px solid #bfdbfe'
      }}>
        <div style={{ fontSize: '12px', fontWeight: '600', color: '#1e40af', marginBottom: '8px' }}>
          💡 Kuwait Pricing Examples:
        </div>
        <div style={{ fontSize: '11px', color: '#1e3a8a', lineHeight: '1.6' }}>
          • Basic Monthly: KD 15.000 (10% discount, 10 items)<br/>
          • Premium Monthly: KD 30.000 (15% discount, 20 items, free express)<br/>
          • Family Quarterly: KD 75.000 (20% discount, 75 items, all benefits)
        </div>
      </div>

      <div className="form-actions">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        <Button type="submit" variant="primary">
          {plan ? 'Update' : 'Create'} Plan
        </Button>
      </div>
    </form>
  );
};

export default SubscriptionPlanForm;